// TanStack Query hooks for /api/vouchers + /api/reports.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface VoucherRow {
  id: string;
  number: string;
  date: string;
  narration?: string | null;
  partyLedgerId?: string | null;
  partyLedger?: { id: string; name: string } | null;
  voucherTypeId: string;
  voucherType?: { id: string; code: string; baseTypeCode?: string | null; name: string; abbreviation: string };
  branchId?: string | null;
  branch?: { id: string; name: string } | null;
  subTotal: string | number;
  totalTaxAmount: string | number;
  totalAmount: string | number;
  roundOff: string | number;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  tallySyncedAt?: string | null;
  irn?: string | null;
  ackNo?: string | null;
  signedQrCode?: string | null;
  eWayBillNumber?: string | null;
  createdAt: string;
  createdBy: string;
}

export interface VoucherLine {
  id: string;
  lineNumber: number;
  itemId?: string | null;
  item?: { id: string; name: string } | null;
  ledgerId?: string | null;
  ledger?: { id: string; name: string } | null;
  description?: string | null;
  hsnSacId?: string | null;
  hsnSac?: { id: string; code: string } | null;
  unitId?: string | null;
  unit?: { id: string; code: string; name: string } | null;
  quantityPrimary?: string | number | null;
  quantitySecondary?: string | number | null;
  ratePerUnit?: string | number | null;
  mrp?: string | number | null;
  discountPercent?: string | number | null;
  discountAmount?: string | number | null;
  taxableValue?: string | number | null;
  cgstPercent?: string | number | null;
  cgstAmount?: string | number | null;
  sgstPercent?: string | number | null;
  sgstAmount?: string | number | null;
  igstPercent?: string | number | null;
  igstAmount?: string | number | null;
  cessPercent?: string | number | null;
  cessAmount?: string | number | null;
  totalAmount?: string | number | null;
}

export interface JournalLineRow {
  id: string;
  ledgerId: string;
  ledger?: { id: string; name: string };
  amount: string | number;
  narration?: string | null;
}

export interface VoucherDetail extends VoucherRow {
  lines: VoucherLine[];
  journalLines: JournalLineRow[];
  stockMovements: Array<{ id: string; itemId: string; direction: 'IN' | 'OUT'; quantity: string | number; ratePerUnit: string | number; totalValue: string | number; item?: { id: string; name: string } }>;
  billRefs: Array<{ id: string; type: string; refName: string; amount: string | number; settledAmount: string | number; settledOn?: string | null; refDate?: string; dueDate?: string | null }>;
}

export interface VoucherListQuery {
  page?: number;
  pageSize?: number;
  voucherTypeId?: string;
  baseTypeCode?: string;
  partyLedgerId?: string;
  fromDate?: string;
  toDate?: string;
  q?: string;
  includeCancelled?: boolean;
  minAmount?: number;
  maxAmount?: number;
}

export function useVouchers(q: VoucherListQuery = {}) {
  return useQuery({
    queryKey: ['vouchers', 'list', q],
    queryFn: async () => {
      const res = await api.get<{ rows: VoucherRow[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }>('/vouchers', { params: q });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useVoucher(id: string | null) {
  return useQuery({
    queryKey: ['vouchers', 'item', id],
    queryFn: async () => {
      const res = await api.get<VoucherDetail>(`/vouchers/${id}`);
      return res.data;
    },
    enabled: Boolean(id),
  });
}

export function useCancelVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const res = await api.post(`/vouchers/${id}/cancel`, { reason });
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['vouchers', 'list'] });
      qc.invalidateQueries({ queryKey: ['vouchers', 'item', vars.id] });
      qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function voucherPdfUrl(id: string, template = 'raaj-classic'): string {
  return `/api/vouchers/${encodeURIComponent(id)}/pdf?template=${encodeURIComponent(template)}`;
}
